import React, { FC } from 'react';
import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import memoize from 'lodash/memoize';

import { useAutoCompleteController } from '@/controllers';
import { MAX_TEXT_LENGTH } from '@/constants';
import { ProductFormData } from '@/components/composed/forms/data-mapper';
import { FormFieldTooltipLabel } from '@/components/primitive';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';

import { helperCopy } from './copy';

type ProductFormProps = {
  isLoading: boolean;
} & Partial<ProductFormData>;

const ProductForm: FC<ProductFormProps> = ({ isLoading, name = '', description = '' }) => {
  const getSearch = useAutoCompleteController('product');

  const validateName = memoize((searchString?: string) => {
    if (!searchString) {
      return 'Name must be provided';
    }

    // user is allowed to use the predefined name
    if (searchString === name) {
      return undefined;
    }

    return getSearch(searchString).then(d => {
      if (
        d.find(({ name: entityName }) => entityName?.toLowerCase() === searchString.toLowerCase())
      ) {
        return 'Name already in use';
      }

      return undefined;
    });
  });

  return (
    <>
      <Field<string>
        name="name"
        label={<FormFieldTooltipLabel isRequired label="Name" content={helperCopy.product.name} />}
        validate={validateName}
        isRequired
        isDisabled={isLoading}
        defaultValue={name}
      >
        {({ fieldProps, error }) => (
          <>
            <TextField
              maxLength={MAX_TEXT_LENGTH}
              placeholder="Name of the product, e.g. Jira Service Management"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<string, HTMLTextAreaElement>
        name="description"
        label="Description"
        defaultValue={description}
        isDisabled={isLoading}
      >
        {({ fieldProps, error }) => (
          <>
            {/* @ts-ignore */}
            <TextArea minimumRows={3} {...fieldProps} maxLength={MAX_TEXT_LENGTH} />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
    </>
  );
};

export default ProductForm;
